import { createAppRequest } from '@/services/request';
import type {
  BuyerCandidate,
  ConditionItem,
  ExitOpportunity,
  ExitProject,
  PaymentPlan,
  PaymentRecord,
  OpportunityStatus,
  ProjectStage,
  TransactionStage,
} from '@/features/exit-management/types';
import type { ExitTransactionApi } from '@/features/exit-management/apiMappers';
import type { InvestmentRelationDto } from '@/services/bff/portfolio.api';

export type ExitTransactionAllocationInput = {
  investmentRelationUuid: string;
  soldHolding: number;
  consideration: number;
  transactionFee?: number;
  taxAmount?: number;
};

const request = createAppRequest();

export type CompletionReadiness = {
  canComplete: boolean;
  blockers: Array<{ code: string; message: string }>;
  receivedAmount: number;
};

export type TransactionReadiness = {
  canProceed: boolean;
  blockers: Array<{ code: string; message: string }>;
};

export const exitApi = {
  listOpportunities: () => request<ExitOpportunity[]>('/bff/exit/opportunities'),

  getOpportunity: (uuid: string) =>
    request<ExitOpportunity>(`/bff/exit/opportunities/${uuid}`),

  updateOpportunityStatus: (uuid: string, status: OpportunityStatus) =>
    request<ExitOpportunity>(`/bff/exit/opportunities/${uuid}/status`, {
      method: 'PATCH',
      data: { status },
    }),

  updateOpportunity: (
    uuid: string,
    data: Partial<Pick<ExitOpportunity, 'signal' | 'recommendedMethod' | 'priority' | 'expectedExitAmount' | 'timeWindow' | 'nextAction' | 'judgmentConclusion'>>,
  ) => request<ExitOpportunity>(`/bff/exit/opportunities/${uuid}`, { method: 'PATCH', data }),

  resumeOpportunity: (uuid: string) =>
    request<ExitOpportunity>(`/bff/exit/opportunities/${uuid}/resume`, { method: 'POST' }),

  convertOpportunity: (uuid: string) =>
    request<ExitProject>(`/bff/exit/opportunities/${uuid}/convert`, {
      method: 'POST',
    }),

  listProjects: () => request<ExitProject[]>('/bff/exit/projects'),

  listInvestmentRelations: (companyUuid: string) =>
    request<InvestmentRelationDto[]>('/bff/exit/relations', {
      params: { companyUuid },
    }),

  getProject: (uuid: string) =>
    request<ExitProject>(`/bff/exit/projects/${uuid}`),

  updateProject: (
    uuid: string,
    data: {
      owner?: string;
      exitMethod?: string;
      exitType?: '全部退出' | '部分退出';
      targetExitAmount?: number;
      nextAction?: string | null;
      /** P1 定价依据 5 字段 */
      exitValuation?: number | null;
      valuationMethod?: string | null;
      valuationDate?: string | null;
      pricingBasis?: string | null;
      plannedTransactionDate?: string | null;
    },
  ) => request<ExitProject>(`/bff/exit/projects/${uuid}`, { method: 'PATCH', data }),

  getTransactionReadiness: (uuid: string) =>
    request<TransactionReadiness>(`/bff/exit/projects/${uuid}/transaction-readiness`),

  selectBuyer: (projectUuid: string, buyerUuid: string) =>
    request<ExitProject>(`/bff/exit/projects/${projectUuid}/select-buyer/${buyerUuid}`, {
      method: 'POST',
    }),

  createTransaction: (
    projectUuid: string,
    data: {
      buyerUuid: string;
      dealAmount: number;
      exitMethod?: string;
      transactionFee?: number;
      taxAmount?: number;
      currency?: string;
      allocations: ExitTransactionAllocationInput[];
    },
  ) => request<ExitTransactionApi>(`/bff/exit/projects/${projectUuid}/transactions`, {
    method: 'POST',
    data,
  }),

  updateProjectStage: (uuid: string, stage: ProjectStage) =>
    request<ExitProject>(`/bff/exit/projects/${uuid}/stage`, {
      method: 'PATCH',
      data: { stage },
    }),

  resumeProject: (uuid: string) =>
    request<ExitProject>(`/bff/exit/projects/${uuid}/resume`, { method: 'POST' }),

  listTransactions: () =>
    request<ExitTransactionApi[]>('/bff/exit/transactions'),

  getTransaction: (uuid: string) =>
    request<ExitTransactionApi>(`/bff/exit/transactions/${uuid}`),

  getCompletionReadiness: (uuid: string) =>
    request<CompletionReadiness>(`/bff/exit/transactions/${uuid}/completion-readiness`),

  updateTransaction: (
    uuid: string,
    data: {
      buyerOrganizationUuid?: string;
      buyerContactPersonUuid?: string;
    },
  ) =>
    request<ExitTransactionApi>(`/bff/exit/transactions/${uuid}`, {
      method: 'PATCH',
      data,
    }),

  updateTransactionStage: (uuid: string, stage: TransactionStage) =>
    request<ExitTransactionApi>(`/bff/exit/transactions/${uuid}/stage`, {
      method: 'PATCH',
      data: { stage },
    }),

  resumeTransaction: (uuid: string) =>
    request<ExitTransactionApi>(`/bff/exit/transactions/${uuid}/resume`, { method: 'POST' }),

  completeTransaction: (uuid: string) =>
    request<{ transaction: ExitTransactionApi }>(
      `/bff/exit/transactions/${uuid}/complete`,
      { method: 'POST' },
    ),

  reverseSettlementCash: (uuid: string, reason: string) =>
    request<{
      transaction: ExitTransactionApi;
      reversedCashCount: number;
      cancelledPendingCount: number;
    }>(`/bff/exit/transactions/${uuid}/reverse-settlement-cash`, {
      method: 'POST',
      data: { reason },
    }),

  completeLegalClosing: (uuid: string) =>
    request<{ transaction: ExitTransactionApi }>(
      `/bff/exit/transactions/${uuid}/legal-closing`,
      { method: 'POST' },
    ),

  finalizeProjectSettlement: (uuid: string) =>
    request<{ project: ExitProject }>(
      `/bff/exit/projects/${uuid}/final-settlement`,
      { method: 'POST' },
    ),

  listBuyers: (projectUuid?: string) =>
    request<BuyerCandidate[]>('/bff/exit/buyers', {
      params: projectUuid ? { projectUuid } : undefined,
    }),

  createBuyer: (data: {
    projectUuid: string;
    name: string;
    organizationUuid?: string;
    contactPersonUuid?: string;
    type?: string;
    source?: string;
    relatedParty?: boolean;
    stage?: string;
  }) =>
    request<BuyerCandidate>('/bff/exit/buyers', {
      method: 'POST',
      data,
    }),

  updateBuyer: (uuid: string, data: Partial<BuyerCandidate>) =>
    request<BuyerCandidate>(`/bff/exit/buyers/${uuid}`, {
      method: 'PATCH',
      data,
    }),

  listConditions: (transactionUuid?: string) =>
    request<ConditionItem[]>('/bff/exit/conditions', {
      params: transactionUuid ? { transactionUuid } : undefined,
    }),

  createCondition: (data: Omit<ConditionItem, 'id'> & { transactionId: string }) =>
    request<ConditionItem>('/bff/exit/conditions', {
      method: 'POST',
      data: {
        transactionUuid: data.transactionId,
        title: data.title,
        owner: data.owner,
        dueDate: data.dueDate,
        required: data.required,
      },
    }),

  updateCondition: (
    uuid: string,
    data: Partial<
      Pick<ConditionItem, 'status' | 'waiverReason' | 'owner' | 'dueDate'>
    >,
  ) =>
    request<ConditionItem>(`/bff/exit/conditions/${uuid}`, {
      method: 'PATCH',
      data,
    }),

  listPaymentPlans: (transactionUuid?: string) =>
    request<PaymentPlan[]>('/bff/exit/payment-plans', {
      params: transactionUuid ? { transactionUuid } : undefined,
    }),

  createPaymentPlan: (data: {
    transactionId: string;
    installment?: string;
    receivableAmount: number;
    plannedDate?: string;
    planType?: 'regular' | 'escrow';
    escrowReleaseDate?: string;
  }) =>
    request<PaymentPlan>('/bff/exit/payment-plans', {
      method: 'POST',
      data: {
        transactionUuid: data.transactionId,
        installment: data.installment,
        receivableAmount: data.receivableAmount,
        plannedDate: data.plannedDate,
        planType: data.planType,
        escrowReleaseDate: data.escrowReleaseDate,
      },
    }),

  updatePaymentPlan: (uuid: string, data: Partial<PaymentPlan>) =>
    request<PaymentPlan>(`/bff/exit/payment-plans/${uuid}`, {
      method: 'PATCH',
      data,
    }),

  releaseEscrowPlan: (uuid: string, data?: { releaseDate?: string; remark?: string }) =>
    request<PaymentPlan>(`/bff/exit/payment-plans/${uuid}/release-escrow`, {
      method: 'POST',
      data: data ?? {},
    }),

  deductEscrowPlan: (uuid: string, data: { deductionAmount: number; reason: string }) =>
    request<PaymentPlan>(`/bff/exit/payment-plans/${uuid}/deduct-escrow`, {
      method: 'POST',
      data,
    }),

  listPaymentRecords: (transactionUuid?: string) =>
    request<PaymentRecord[]>('/bff/exit/payment-records', {
      params: transactionUuid ? { transactionUuid } : undefined,
    }),

  createPaymentRecord: (data: {
    paymentPlanId: string;
    transactionId: string;
    amount: number;
    receivedAt?: string;
    remark?: string;
    proofAttachmentUuid: string;
  }) =>
    request<PaymentRecord>('/bff/exit/payment-records', {
      method: 'POST',
      data: {
        paymentPlanUuid: data.paymentPlanId,
        transactionUuid: data.transactionId,
        amount: data.amount,
        receivedAt: data.receivedAt,
        remark: data.remark,
        proofAttachmentUuid: data.proofAttachmentUuid,
      },
    }),

  verifyPaymentProof: (uuid: string) =>
    request<PaymentRecord>(`/bff/exit/payment-records/${uuid}/verify-proof`, {
      method: 'POST',
    }),

  reversePaymentRecord: (uuid: string, reason: string) =>
    request<PaymentRecord>(`/bff/exit/payment-records/${uuid}/reverse`, {
      method: 'POST',
      data: { reason },
    }),
};
